import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { Category, CategoryTranslation, adminService } from '../services/adminService'
import { TranslationProgress } from '../utils/constants'
import { TranslationProgressModal } from './TranslationProgressModal'
import { AITranslationModal } from './AITranslationModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { translationService } from '../services/translationService'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { LanguageTabs } from './LanguageTabs'

interface CategoryFormProps {
  category?: Category | null
  onSubmit: (data: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => void
  onCancel: () => void
  onDelete?: (category: Category) => void
  isLoading: boolean
}

interface CategoryFormData {
  icon: string
  status: 'active' | 'inactive' | 'draft' | 'archived'
  order: number
  translations: Record<string, { name: string; description: string }>
}

export function CategoryForm({ category, onSubmit, onCancel, onDelete, isLoading }: CategoryFormProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('en')
  const [showAIModal, setShowAIModal] = useState(false)
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [translationProgress, setTranslationProgress] = useState<Record<string, TranslationProgress>>({})
  const queryClient = useQueryClient()
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  // Load existing translations
  const { data: translations = [], isLoading: translationsLoading } = useQuery({
    queryKey: ['categoryTranslations', category?.id],
    queryFn: () => category?.id ? adminService.getCategoryTranslations(category.id) : Promise.resolve([]),
    enabled: !!category?.id,
  })

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<CategoryFormData>({
    defaultValues: {
      icon: category?.icon || '',
      status: category?.status || 'active',
      order: category?.order || 0,
      translations: {}
    }
  })

  // Initialize form with translations when they load
  useEffect(() => {
    // Skip if translations are still loading
    if (category?.id && translationsLoading) {
      return
    }

    console.log('🔄 Initializing form with translations:', translations)

    const translationsMap: Record<string, { name: string; description: string }> = {}
    
    // Initialize empty values for all supported languages first
    SUPPORTED_LANGUAGES.forEach(lang => {
      translationsMap[lang.code] = {
        name: '',
        description: ''
      }
    })
    
    // Add existing translations (overwriting empty values where data exists)
    if (translations.length > 0) {
      translations.forEach(translation => {
        translationsMap[translation.language] = {
          name: translation.name,
          description: translation.description
        }
        console.log(`📝 Setting translation for ${translation.language}:`, translation)
      })
    } else if (category) {
      // If no translations exist but category has name/description, use as English
      translationsMap.en = {
        name: category.name || '',
        description: category.description || ''
      }
      console.log('📝 Using category data as English translation:', translationsMap.en)
    }

    // Reset the entire form with new values
    reset({
      icon: category?.icon || '',
      status: category?.status || 'active',
      order: category?.order || 0,
      translations: translationsMap
    })

    console.log('✅ Form reset with translations:', translationsMap)
  }, [translations, category?.id, translationsLoading])

  const watchedTranslations = watch('translations')

  // Note: Removed tab switching effect as it was causing fields to clear unnecessarily
  // The form should maintain all translation values and just show/hide the appropriate tab content

  const createTranslationMutation = useMutation({
    mutationFn: (translation: Omit<CategoryTranslation, 'id'>) =>
      adminService.createCategoryTranslation(translation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoryTranslations', category?.id] })
    },
  })

  const updateTranslationMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CategoryTranslation> }) =>
      adminService.updateCategoryTranslation(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoryTranslations', category?.id] })
    },
  })

  const handleFormSubmit = async (data: CategoryFormData) => {
    try {
      // Get the English translation as the main category data
      const englishTranslation = data.translations.en || { name: '', description: '' }
      
      // Submit the main category data
      await onSubmit({
        name: englishTranslation.name,
        description: englishTranslation.description,
        icon: data.icon,
        status: data.status,
        order: data.order,
        language: 'en'
      })

      // If editing, save all translations
      if (category?.id) {
        for (const [languageCode, translation] of Object.entries(data.translations)) {
          if (translation.name && translation.description) {
            const existingTranslation = translations.find(t => t.language === languageCode)
            
            if (existingTranslation) {
              await updateTranslationMutation.mutateAsync({
                id: existingTranslation.id,
                updates: {
                  name: translation.name,
                  description: translation.description
                }
              })
            } else {
              await createTranslationMutation.mutateAsync({
                categoryId: category.id,
                language: languageCode,
                name: translation.name,
                description: translation.description
              })
            }
          }
        }
      }
    } catch (error) {
      console.error('Error saving category:', error)
      toast.error(t('messages.categoryUpdateFailed'))
    }
  }

  const handleAITranslate = async (sourceLanguage: string, targetLanguages: string[], maxConcurrency: number = 3) => {
    const sourceTranslation = watchedTranslations[sourceLanguage]
    
    if (!sourceTranslation?.name || !sourceTranslation?.description) {
      toast.error(t('messages.sourceLanguageRequired'))
      return
    }

    // Initialize progress tracking
    const progress: Record<string, TranslationProgress> = {}
    targetLanguages.forEach(lang => {
      progress[lang] = {
        language: lang,
        status: 'pending',
      }
    })
    setTranslationProgress(progress)
    setShowProgressModal(true)

    // Configure translation service
    translationService.setConfig({
      temperature: 1.3,
      useJsonMode: true
    })

    // Prepare translation requests for parallel processing
    const requests = targetLanguages.map(lang => ({
      sourceLanguage,
      targetLanguage: lang,
      text: {
        name: sourceTranslation.name,
        description: sourceTranslation.description
      },
      temperature: 1.3,
      useJsonMode: true
    }))

    // Start all translations and mark them as translating
    targetLanguages.forEach(lang => {
      setTranslationProgress(prev => ({
        ...prev,
        [lang]: { ...prev[lang], status: 'translating', startTime: Date.now() }
      }))
    })

    try {
      // Process translations in parallel with progress tracking
      await translationService.translateParallel(
        requests,
        (completed, _, result, error, requestIndex) => {
          // Use the correct requestIndex instead of completed - 1
          const lang = targetLanguages[requestIndex!]
          
          if (error) {
            setTranslationProgress(prev => ({
              ...prev,
              [lang]: { 
                ...prev[lang], 
                status: 'error', 
                endTime: Date.now(),
                error: error instanceof Error ? error.message : 'Translation failed'
              }
            }))
            
            const languageName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang
            toast.error(t('messages.translationFailed', { language: languageName }))
          } else if (result) {
            // Update form with translated content
            setValue(`translations.${lang}`, {
              name: result.name,
              description: result.description
            })

            // Update progress to completed
            setTranslationProgress(prev => ({
              ...prev,
              [lang]: { 
                ...prev[lang], 
                status: 'completed', 
                endTime: Date.now(),
                translatedText: result.name
              }
            }))

            const languageName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang
            
            // Auto-save translation to database if category exists
            if (category?.id) {
              // Update progress to saving
              setTranslationProgress(prev => ({
                ...prev,
                [lang]: { 
                  ...prev[lang], 
                  status: 'saving'
                }
              }))

                             // Save to database
               adminService.createCategoryTranslation({
                 categoryId: category.id,
                 language: lang,
                 name: result.name,
                 description: result.description || ''
               }).then(() => {
                // Update progress to saved
                setTranslationProgress(prev => ({
                  ...prev,
                  [lang]: { 
                    ...prev[lang], 
                    status: 'saved'
                  }
                }))
                
                // Invalidate cache to refresh translations
                queryClient.invalidateQueries({ queryKey: ['categoryTranslations', category.id] })
                
                toast.success(t('messages.translationSaved', { language: languageName }))
              }).catch((saveError) => {
                console.error(`Failed to save translation for ${lang}:`, saveError)
                setTranslationProgress(prev => ({
                  ...prev,
                  [lang]: { 
                    ...prev[lang], 
                    status: 'error',
                    error: 'Failed to save to database'
                  }
                }))
                
                toast.error(t('messages.translationSaveFailed', { language: languageName }))
              })
            } else {
              toast.success(t('messages.translationCompleted', { language: languageName }))
            }
          }
        },
        maxConcurrency // Use configured concurrency
      )

      // Show final status based on saved translations
      const finalProgress = Object.values(translationProgress)
      const savedCount = finalProgress.filter(p => p.status === 'saved').length
      const errorCount = finalProgress.filter(p => p.status === 'error').length
      
      if (savedCount > 0) {
        toast.success(t('messages.translationBatchSuccess', { count: savedCount, total: targetLanguages.length }))
      }
      if (errorCount > 0) {
        toast.error(t('messages.translationBatchError', { count: errorCount, total: targetLanguages.length }))
      }
      
    } catch (error) {
      console.error('Parallel translation failed:', error)
      toast.error(t('messages.translationProcessFailed'))
    }
  }

  const handleDelete = () => {
    if (category && onDelete) {
      setShowDeleteConfirm(true)
    }
  }

  const handleDeleteConfirm = () => {
    if (category && onDelete) {
      onDelete(category)
      setShowDeleteConfirm(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  const getTranslationStatus = (languageCode: string) => {
    const translation = watchedTranslations[languageCode]
    const progress = translationProgress[languageCode]
    
    if (progress) {
      switch (progress.status) {
        case 'pending': return { color: 'text-yellow-600', text: 'Pending...' }
        case 'translating': return { color: 'text-blue-600', text: 'Translating...' }
        case 'completed': return { color: 'text-green-600', text: `Done in ${progress.endTime && progress.startTime ? Math.round((progress.endTime - progress.startTime) / 1000) : 0}s` }
        case 'error': return { color: 'text-red-600', text: 'Error' }
      }
    }
    
    if (translation?.name && translation?.description) {
      return { color: 'text-green-600', text: 'Complete' }
    }
    
    return { color: 'text-gray-400', text: 'Empty' }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
        {/* Basic Category Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('challengeEdit.basicInformation')}
            </h2>
            <button
              type="button"
              onClick={() => setShowAIModal(true)}
              className="bg-gray-600 text-white px-4 py-2 rounded-full hover:bg-gray-700 transition-colors text-sm shadow"
            >
              {t('categories.actions.aiTranslate')}
            </button>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('categories.fields.icon')}
            </label>
            <input
              type="text"
              {...register('icon', { required: 'Icon is required' })}
              placeholder={t('categories.placeholders.icon')}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-10"
            />
            {errors.icon && (
              <p className="text-red-500 text-sm mt-1">{errors.icon.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.status')}
            </label>
            <select
              {...register('status', { required: 'Status is required' })}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-10"
            >
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('categories.fields.order')}
            </label>
            <input
              type="number"
              {...register('order', { required: 'Order is required', valueAsNumber: true })}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-10"
            />
            {errors.order && (
              <p className="text-red-500 text-sm mt-1">{errors.order.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Multi-language Content */}
      <div className="bg-white rounded-lg shadow">
        <LanguageTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          translations={watchedTranslations}
        />

        <div className="p-6">
          <div className="space-y-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('translations.fields.name')} ({SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name})
                </label>
                <input
                  type="text"
                  {...register(`translations.${activeTab}.name` as const)}
                  key={`name-${activeTab}-${category?.id || 'new'}`}
                  placeholder={t('categories.placeholders.name', { language: SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name })}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('translations.fields.description')} ({SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name})
                </label>
                <textarea
                  rows={3}
                  {...register(`translations.${activeTab}.description` as const)}
                  key={`description-${activeTab}-${category?.id || 'new'}`}
                  placeholder={t('categories.placeholders.description', { language: SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name })}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-between">
        {/* Delete button - only show when editing an existing category */}
        {category && onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 text-red-700 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 hover:border-red-300 transition-colors"
          >
            {t('categories.actions.deleteCategory')}
          </button>
        )}
        
        {/* Form actions */}
        <div className="flex space-x-3 ml-auto">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-gray-600 text-white rounded-full hover:bg-gray-700 disabled:opacity-50 transition-colors shadow"
          >
            {isLoading ? t('common.loading') : category ? t('common.update') : t('common.create')}
          </button>
        </div>
      </div>
      </form>

      {showAIModal && (
        <AITranslationModal
          availableLanguages={SUPPORTED_LANGUAGES}
          existingTranslations={Object.keys(watchedTranslations).filter(
            lang => watchedTranslations[lang]?.name && watchedTranslations[lang]?.description
          )}
          onTranslate={(sourceLanguage, targetLanguages, maxConcurrency) => {
            setShowAIModal(false)
            handleAITranslate(sourceLanguage, targetLanguages, maxConcurrency)
          }}
          onClose={() => setShowAIModal(false)}
        />
      )}
      
      <TranslationProgressModal
        isOpen={showProgressModal}
        progress={translationProgress}
        onClose={() => setShowProgressModal(false)}
        title={t('categories.aiTranslation.progressTitle')}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('categories.deleteConfirmTitle')}
        message={t('categories.deleteConfirmMessage', { name: category?.name || category?.id })}
        warningText={t('categories.deleteWarningText')}
        isLoading={false}
        destructive={true}
      />
    </div>
  )
} 